import React, { useEffect, useState } from "react";
import { Typography, Button, Card, Form, Input, Spin, Result } from "antd";
import { useNavigate, useParams, NavLink } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  Calendar,
  Clock,
  RefreshCw,
  User,
} from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import MeetingSuccessState from "./MeetingSuccessState";
import { getMethod, deleteMethod } from "../../services/HttpServices";
import { publicBooking } from "../../constant/Constant";
import LogoSvg from "../../assets/svg/logos/logo-black.svg";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;
const { TextArea } = Input;
const fallbackTimezone = dayjs.tz.guess();

const formatDateTimeInTimezone = (value, activeTimezone, format) => {
  if (!value) return "";
  return dayjs(value)
    .tz(activeTimezone || fallbackTimezone)
    .format(format);
};

const formatTimeSlotRange = (timeSlot, duration) => {
  if (!timeSlot) return "-";
  const normalizedSlot =
    timeSlot.includes(":") && timeSlot.length === 5
      ? `${timeSlot}:00`
      : timeSlot;
  const start = dayjs(`2024-01-01T${normalizedSlot}`);

  if (!start.isValid()) return timeSlot;

  return `${start.format("hh:mm A")} - ${start.add(duration || 30, "minute").format("hh:mm A")}`;
};

const formatUtcTimePartInSystemTimezone = (value, format = "HH:mm:ss") => {
  if (!value) return "";

  const match = String(value).match(/T(\d{2}:\d{2}:\d{2})/);
  if (!match) return "";

  return dayjs
    .utc(`2000-01-01T${match[1]}Z`)
    .tz(fallbackTimezone)
    .format(format);
};

const CancelMeeting = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [isCancelled, setIsCancelled] = useState(false);
  const [meetingDetails, setMeetingDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const resolvedTimezone = meetingDetails?.timezone || fallbackTimezone;

  useEffect(() => {
    if (!id) return;
    getMethod(`${publicBooking}/manage/${id}`)
      .then((res) => {
        setMeetingDetails(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load generic booking info:", err);
        setErrorStatus(true);
        setLoading(false);
      });
  }, [id]);

  const handleCancelSubmit = () => {
    setDeleting(true);
    deleteMethod(`${publicBooking}/${id}`)
      .then(() => {
        setDeleting(false);
        setIsCancelled(true);
      })
      .catch(() => {
        setDeleting(false);
      });
  };

  return (
    <div className="tw-min-h-screen tw-bg-gradient-to-tr tw-from-[#f4f2ff] tw-via-[#f8f9fd] tw-to-[#fdfdff] tw-py-12 tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-flex tw-items-center tw-justify-center">
      <div className="tw-w-full tw-max-w-[840px] tw-bg-white tw-rounded-3xl tw-border tw-border-solid tw-border-slate-100 tw-shadow-[0_24px_70px_rgba(93,68,255,0.07)] tw-p-6 sm:tw-p-10">
        {isCancelled ? (
          <MeetingSuccessState
            title="Meeting Cancelled Successfully"
            description="The meeting has been removed from the BeyondLeadz calendar."
            summaryTitle="Cancelled Meeting"
            status="cancelled"
            summaryRows={[
              {
                label: "Name",
                value: meetingDetails ? meetingDetails.guestName : "",
              },
              {
                label: "Date",
                value: meetingDetails
                  ? formatDateTimeInTimezone(
                      meetingDetails.dueDate,
                      resolvedTimezone,
                      "MMMM D, YYYY",
                    )
                  : "",
              },
              {
                label: "Time Slot",
                value: meetingDetails
                  ? formatTimeSlotRange(
                      formatUtcTimePartInSystemTimezone(
                        meetingDetails.dueDate,
                        "HH:mm:ss",
                      ),
                      meetingDetails.durationMinutes || 30,
                    )
                  : "",
              },
              {
                label: "Type",
                value: meetingDetails ? meetingDetails.meetingName : "",
              },
            ]}
            actions={[
              {
                icon: <RefreshCw size={14} />,
                label: "Schedule Meeting",
                onClick: () => {
                  if (meetingDetails?.customUrl) {
                    navigate(`/schedule-meeting/${meetingDetails.customUrl}`);
                  }
                },
              },
            ]}
          />
        ) : loading ? (
          <div className="tw-py-20 tw-text-center">
            <Spin size="large" />
          </div>
        ) : errorStatus || !meetingDetails ? (
          <div className="tw-text-center tw-py-10">
            <Result
              status="404"
              title="Not Found"
              subTitle="Booking details could not be found."
            />
          </div>
        ) : (
          <>
            <div className="tw-mb-8 tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-4 tw-border-b tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-slate-100 tw-pb-6">
              <div className="tw-flex tw-items-center tw-gap-4">
                <div className="tw-flex tw-h-[56px] tw-w-[56px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-2xl tw-border tw-border-solid tw-border-indigo-100/80 tw-bg-indigo-50/50 tw-text-[28px] tw-text-text-primary tw-shadow-sm">
                  <Calendar className="tw-h-7 tw-w-7 tw-text-[#5d44ff]" />
                </div>
                <div>
                  <Title
                    level={2}
                    className="!tw-mb-0 !tw-text-xl sm:!tw-text-2xl !tw-font-extrabold !tw-text-slate-800"
                  >
                    Cancel Meeting
                  </Title>
                  <Text className="tw-text-sm sm:tw-text-base tw-text-slate-500 tw-mt-0.5 tw-block">
                    Review the meeting details and confirm cancellation.
                  </Text>
                </div>
              </div>
              <div className="tw-flex-shrink-0 tw-pt-1">
                <NavLink to={isAuthenticated ? "/dashboard-view" : "/"} className="hover:tw-opacity-80 tw-transition-opacity">
                  <img
                    src={LogoSvg}
                    alt="BeyondLeadz"
                    style={{ height: "22px", width: "auto" }}
                    className="tw-object-contain tw-cursor-pointer"
                  />
                </NavLink>
              </div>
            </div>

            <div className="tw-grid tw-grid-cols-1 tw-gap-8 lg:tw-grid-cols-[0.95fr_1.05fr]">
              <Card className="!tw-rounded-2xl !tw-border-slate-100/80 tw-shadow-[0_4px_20px_rgba(0,0,0,0.02)]">
                <div className="tw-mb-5 tw-text-[15px] tw-font-bold tw-text-slate-800">
                  Existing Meeting Details
                </div>

                <div className="tw-flex tw-flex-col tw-gap-5">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <User size={16} className="tw-mt-1 tw-text-[#5d44ff]" />
                    <div>
                      <div className="tw-text-xs tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wider">
                        Guest
                      </div>
                      <div className="tw-font-bold tw-text-slate-800 tw-text-sm tw-mt-0.5">
                        {meetingDetails.guestName}
                      </div>
                      <div className="tw-text-xs tw-text-slate-500 tw-mt-0.5">
                        {meetingDetails.guestEmail}
                      </div>
                    </div>
                  </div>

                  <div className="tw-flex tw-items-start tw-gap-3">
                    <Calendar size={16} className="tw-mt-1 tw-text-[#5d44ff]" />
                    <div>
                      <div className="tw-text-xs tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wider">
                        Date
                      </div>
                      <div className="tw-font-bold tw-text-slate-800 tw-text-sm tw-mt-0.5">
                        {formatDateTimeInTimezone(
                          meetingDetails.dueDate,
                          resolvedTimezone,
                          "MMMM D, YYYY",
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="tw-flex tw-items-start tw-gap-3">
                    <Clock size={16} className="tw-mt-1 tw-text-[#5d44ff]" />
                    <div>
                      <div className="tw-text-xs tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wider">
                        Time Slot
                      </div>
                      <div className="tw-font-bold tw-text-slate-800 tw-text-sm tw-mt-0.5">
                        {formatTimeSlotRange(
                          formatUtcTimePartInSystemTimezone(
                            meetingDetails.dueDate,
                            "HH:mm:ss",
                          ),
                          meetingDetails.durationMinutes || 30,
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="tw-rounded-xl tw-bg-slate-50/50 tw-border tw-border-solid tw-border-slate-100 tw-p-4 tw-mt-2">
                    <div className="tw-text-xs tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wider">
                      Meeting Type
                    </div>
                    <div className="tw-font-bold tw-text-slate-800 tw-text-sm tw-mt-0.5">
                      {meetingDetails.meetingName}
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="!tw-rounded-2xl !tw-border-slate-100/80 tw-shadow-[0_4px_20px_rgba(0,0,0,0.02)]">
                <div className="tw-mb-5 tw-text-[15px] tw-font-bold tw-text-slate-800">
                  Cancellation Details
                </div>

                <Form layout="vertical" onFinish={handleCancelSubmit}>
                  <Form.Item
                    name="reason"
                    label={
                      <span className="tw-text-sm tw-font-semibold tw-text-slate-600">
                        Reason for Cancellation
                      </span>
                    }
                    rules={[
                      {
                        required: true,
                        message: "Please enter the cancellation reason",
                      },
                    ]}
                  >
                    <TextArea
                      rows={5}
                      placeholder="Enter cancellation reason..."
                      className="!tw-rounded-xl !tw-border-slate-200"
                    />
                  </Form.Item>

                  <div className="tw-flex tw-justify-end tw-mt-6">
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={deleting}
                      className="tw-h-[44px] !tw-rounded-xl !tw-border-0 !tw-bg-red-500 tw-px-6 tw-font-bold tw-text-sm tw-text-white tw-shadow-[0_6px_16px_rgba(239,68,68,0.25)] hover:tw-shadow-[0_10px_20px_rgba(239,68,68,0.35)] hover:tw-scale-[1.01] hover:!tw-bg-red-600 tw-transition-all tw-duration-200"
                    >
                      Confirm Cancellation
                    </Button>
                  </div>
                </Form>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CancelMeeting;
